// CollaborationWorkspacePage.tsx

import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import {
  Box,
  Tabs,
  Loader,
  Center,
  Text,
  Button,
  Flex,
  Stack,
  Tooltip,
  useMantineTheme,
  Alert,
  ActionIcon,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import type { CSSProperties } from "react";
import TitleComponent from "@/components/shared/TitleComponent";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import { useUserContext } from "@/context/UserContext";
import SharedDocumentsTab from "./SharedDocumentsTab";
import MatchedProfileView from "@/pages/profile/MatchedView";
import AssessmentTab from "./components/assessment/AssessmentTab";
import PaymentMethodModal from "@/pages/interests/components/PaymentMethodModal";
import CollaborationPaymentSuccessModal from "@/pages/interests/components/CollaborationPaymentSuccessModal";
import { useCollaborationEngagementPayment } from "@/pages/interests/hooks/useCollaborationEngagementPayment";
import CollaborationPaymentHeaderCard from "@/pages/collaborations/components/CollaborationPaymentHeaderCard";
import WorkspacePartnerSection from "@/pages/collaborations/components/WorkspacePartnerSection";
import WorkspacePartnerProfileCard from "@/pages/collaborations/components/WorkspacePartnerProfileCard";
import WorkspaceFeatureLockedPanel from "@/pages/collaborations/components/WorkspaceFeatureLockedPanel";
import CollaborationWorkspaceSkeleton from "@/pages/collaborations/components/CollaborationWorkspaceSkeleton";
import { PartnershipHandshakeIcon } from "@/components/icons/PartnershipHandshakeIcon";
import {
  IconArrowLeft,
  IconBuilding,
  IconDownload,
  IconFileText,
  IconInfoCircle,
} from "@tabler/icons-react";
import { CollaborationStatus } from "@/components/shared/constants/general.const";
import { isCollaborationPaymentPhase } from "@/shared/utils/collaboration-helpers";
import {
  COLLABORATIONS_RETURN_TO,
  resolveWorkspaceBackNavigation,
} from "@/shared/utils/workspaceBackNavigation.util";
import {
  NAV_TAB_EQUAL_WIDTH_CELL_STYLE,
  NAV_TABS_FULL_WIDTH_LINE_STYLE,
  NAV_TAB_LABEL_FZ,
  navTabsListStyles,
  navyNavTabSegmentBorders,
} from "@/shared/styles/navTabsLayout";
import { generateCollaborationAlignmentReport } from "@/utils/pdfGenerator";
import { gradeFullQuestionnaire } from "@/utils/gradingEngine";
import { getQuestionnaireId, tryGetQuestionnaireId } from "@/utils/questionnaireMapping";
import type { FullGradingResult, Answer } from "@/utils/gradingEngine.types";
import { QUESTIONNAIRE_MAP } from "@/data/questionnaires/questionnaireRegistry";

const CFC_TAB_TOOLTIP =
  "Partner Readiness is a short questionnaire designed to better understand your expectations for this collaboration. Based on your responses, it evaluates compatibility with your partner, provides a score, and highlights areas where you align as well as topics that may need further discussion.";

const WORKSPACE_TABS = new Set(["overview", "documents", "assessment"]);

function tabFromSearchParams(searchParams: URLSearchParams): string | null {
  const t = searchParams.get("tab");
  if (t === "nda") return "documents";
  if (t === "questionnaire" || t === "chat") return "assessment";
  return t && WORKSPACE_TABS.has(t) ? t : null;
}

/** Tabler / custom SVG size in mobile-only tab strip (icons are hidden on `sm+`). */
const COLLAB_MOBILE_TAB_ICON = 28;


export default function CollaborationWorkspacePage() {
  const { id } = useParams() as { id: string };
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const discoverReturnTo = (location.state as { discoverReturnTo?: string } | null)?.discoverReturnTo;
  const workspaceBack = useMemo(
    () => resolveWorkspaceBackNavigation(discoverReturnTo, COLLABORATIONS_RETURN_TO),
    [discoverReturnTo],
  );
  const { userDetails, hasProfile, refreshCollaborationSidebarCounts } = useUserContext();
  const theme = useMantineTheme();
  /** Matches `hiddenFrom="sm"` / mobile icon strip (below Mantine `sm`). Nested `@media` in `styles.list` is unreliable here. */
  const isWorkspaceTabsMobile = useMediaQuery(
    `(max-width: calc(${theme.breakpoints.sm} - 0.0625em))`,
    false,
    { getInitialValueInEffect: true }
  );

  const collaborationWorkspaceTabsListStyles = useMemo(
    () => ({
      list: isWorkspaceTabsMobile
        ? {
          display: "grid" as const,
          gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          alignItems: "stretch" as const,
          boxShadow: "none" as const,
          gap: 0,
          flexShrink: 0,
        }
        : {
          ...navTabsListStyles(3, { flexShrink: 0 }).list,
        },
    }),
    [isWorkspaceTabsMobile]
  );

  const collaborationWorkspaceTabStyles = useMemo(
    () => ({
      tab: {
        display: "flex" as const,
        alignItems: "center" as const,
        ...(isWorkspaceTabsMobile
          ? {
            flexDirection: "column" as const,
            justifyContent: "center" as const,
            alignItems: "center" as const,
            alignSelf: "stretch" as const,
            minHeight: 56,
            paddingTop: "var(--mantine-spacing-xs)",
            paddingBottom: "var(--mantine-spacing-xs)",
          }
          : {}),
      },
      tabLabel: {
        flex: 1,
        textAlign: "center" as const,
      },
      tabSection: {
        marginInlineStart: 0,
      },
    }),
    [isWorkspaceTabsMobile]
  );

  const initialTab = tabFromSearchParams(searchParams) ?? "overview";
  const [activeTab, setActiveTab] = useState<string | null>(initialTab);

  const autoFocusFirstUnansweredQuestion = useMemo(() => {
    const t = searchParams.get("tab");
    return t === "questionnaire" || t === "assessment";
  }, [searchParams]);

  const handleWorkspaceTabChange = useCallback(
    (value: string | null) => {
      setActiveTab(value);
      if (value === "assessment") {
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.set("tab", "assessment");
            return next;
          },
          { replace: true }
        );
      } else {
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.delete("tab");
            return next;
          },
          { replace: true }
        );
      }
    },
    [setSearchParams]
  );

  // Loading states
  const [loadingCollaboration, setLoadingCollaboration] = useState(true);
  const [workspaceRefreshing, setWorkspaceRefreshing] = useState(false);
  const [loadingCompany, setLoadingCompany] = useState(false);

  // Data states
  const [collaboration, setCollaboration] = useState<any>(null);
  const [company, setCompany] = useState<any>(null);
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  const [questionnaireMySubmitted, setQuestionnaireMySubmitted] = useState<boolean | null>(null);

  // Derived state
  const otherCompanyId =
    collaboration?.requesterCompanyId === userDetails?.company?.id
      ? collaboration?.receiverCompanyId
      : collaboration?.requesterCompanyId;

  // 1. Fetch Collaboration (Always needed for context)
  const fetchCollaboration = useCallback(async () => {
    if (!hasProfile || !id) return;
    setLoadingCollaboration(true);
    try {
      const collabData = await trpc.collaboration.getById.query({ matchId: id });
      setCollaboration(collabData);
    } catch (err) {
      console.error("Error fetching collaboration:", err);
      handleTrpcError(err);
    } finally {
      setLoadingCollaboration(false);
    }
  }, [id, hasProfile]);

  const handleRefreshAfterPayment = useCallback(async () => {
    // Refresh collaboration state and sidebar counts so UI unlocks immediately.
    await fetchCollaboration();
    refreshCollaborationSidebarCounts();
  }, [fetchCollaboration, refreshCollaborationSidebarCounts]);

  const {
    engagementFeeCredits,
    paymentModalOpened,
    paymentLoading,
    beginEngagementPayment,
    handlePaymentModalClose,
    handlePaymentConfirm,
    successData,
    handleSuccessModalClose,
  } = useCollaborationEngagementPayment(handleRefreshAfterPayment);

  // 2. Fetch Company (Lazy load)
  // When workspace is locked (pre-payment), we must not call `company.getById` (it 403s by design).
  // Use `company.publicGetById` to show anonymized/preview data until BOTH_PAID.
  const fetchCompany = useCallback(async (companyId: string, opts?: { allowFull?: boolean }) => {
    setLoadingCompany(true);
    try {
      const allowFull = opts?.allowFull ?? true;
      const companyData = allowFull
        ? await trpc.company.getById.query({ companyId })
        : await trpc.company.publicGetById.query({ companyId });
      setCompany(companyData);
    } catch (err) {
      console.error("Error fetching company:", err);
      handleTrpcError(err);
    } finally {
      setLoadingCompany(false);
    }
  }, []);

  useEffect(() => {
    setQuestionnaireMySubmitted(null);
    setCollaboration(null);
    setCompany(null);
    setLoadingCollaboration(true);
    const t = tabFromSearchParams(searchParams);
    setActiveTab(t ?? "overview");
  }, [id]);

  useEffect(() => {
    if (searchParams.get("tab") !== "nda") return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("tab");
        return next;
      },
      { replace: true }
    );
  }, [searchParams, setSearchParams]);

  const collaborationHasQuestionnaire = useMemo(() => {
    if (collaboration?.partnerReadinessQuestionnaireId) return true;
    return tryGetQuestionnaireId(collaboration?.requesterRole, collaboration?.expectedPartnerRole) !== null;
  }, [
    collaboration?.partnerReadinessQuestionnaireId,
    collaboration?.requesterRole,
    collaboration?.expectedPartnerRole,
  ]);

  const fetchQuestionnaireMySubmission = useCallback(async () => {
    if (!id) return;
    try {
      const data = await trpc.collaboration.getQuestionnaireResponses.query({ matchId: id });
      setQuestionnaireMySubmitted(!!data.mySubmittedAt);
    } catch {
      setQuestionnaireMySubmitted(null);
    }
  }, [id]);

  useEffect(() => {
    if (!collaboration?.id || collaboration.id !== id || activeTab !== "overview") return;
    fetchQuestionnaireMySubmission();
  }, [collaboration?.id, id, activeTab, fetchQuestionnaireMySubmission]);

  const handleGoToQuestionnaireTab = useCallback(() => {
    setActiveTab("assessment");
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("tab", "assessment");
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  const handleMatchCreated = useCallback(
    (newMatchId: string) => {
      setWorkspaceRefreshing(true);
      navigate(`/collaborations/${newMatchId}`, { replace: true, state: workspaceBack });
    },
    [navigate, workspaceBack]
  );

  useEffect(() => {
    if (!workspaceRefreshing) return;
    setWorkspaceRefreshing(false);
  }, [id, workspaceRefreshing]);

  // Effect: Fetch Collaboration on mount
  useEffect(() => {
    fetchCollaboration();
  }, [fetchCollaboration]);

  useEffect(() => {
    if (!otherCompanyId || loadingCompany) return;
    const wantFull = collaboration?.status === CollaborationStatus.BOTH_PAID;
    const hasFullSignal = Boolean(
      company && ((company as any).email || (company as any).phoneNumber || (company as any).website)
    );
    if (!company || (wantFull && !hasFullSignal)) {
      fetchCompany(otherCompanyId, { allowFull: wantFull });
    }
  }, [otherCompanyId, company, loadingCompany, fetchCompany, collaboration?.status]);

  if (loadingCollaboration || workspaceRefreshing) {
    return (
      <Box mih="100vh" pb={20}>
        <TitleComponent title="Collaboration Workspace" />
        <CollaborationWorkspaceSkeleton />
      </Box>
    );
  }

  if (!collaboration) {
    return (
      <Box mih="100vh" pb={20}>
        <TitleComponent title="Collaboration Workspace" />
        <Center py="xl" mih="50vh">
          <Stack gap="md" align="center" maw={400}>
            <Text c="slate.6" ta="center">
              This collaboration is no longer available. It may have been replaced by a new interest request.
            </Text>
            <Button variant="filled" color="primary" onClick={() => navigate(workspaceBack.path)}>
              {workspaceBack.label}
            </Button>
          </Stack>
        </Center>
      </Box>
    );
  }

  const bothPaid = collaboration.status === CollaborationStatus.BOTH_PAID;
  const workspaceLocked = !bothPaid;
  const effectiveReadOnly = workspaceLocked || !!collaboration.collaborationReadOnly;
  const isRequesterCompany = collaboration.requesterCompanyId === userDetails?.company?.id;
  const userPaid = isRequesterCompany ? Boolean(collaboration.requesterPaid) : Boolean(collaboration.receiverPaid);
  const showPreviewPaymentBanner = workspaceLocked && isCollaborationPaymentPhase(collaboration);
  /** Yellow payment banner → tabs → anonymized profile card. */
  const showPartnerCardBelowTabs = workspaceLocked && showPreviewPaymentBanner && Boolean(company);
  const showPartnerSectionAboveTabs = Boolean(company) && workspaceLocked && !showPreviewPaymentBanner;
  /** Avoid duplicate legal-name header in Overview when workspace renders its own partner card. */
  const suppressMatchedProfileHeader = workspaceLocked && Boolean(company);

  const handleWorkspaceBack = () => {
    navigate(workspaceBack.path);
  };

  const handleDownloadProfile = async () => {
    if (workspaceLocked) return;
    setDownloadingPDF(true);
    try {
      // Fetch Party A (own company)
      const partyACompany = await trpc.company.getById.query({ companyId: userDetails!.company!.id });

      // Fetch Party B (other company), reuse if already loaded
      let partyBCompany = company;
      if (!partyBCompany && otherCompanyId) {
        partyBCompany = await trpc.company.getById.query({ companyId: otherCompanyId });
        setCompany(partyBCompany);
      }

      if (!partyACompany || !partyBCompany) return;

      // Fetch shared documents
      const sharedDocs = await trpc.collaboration.listDocuments.query({ matchId: id });

      const collaborationType =
        collaboration?.requesterRole && collaboration?.expectedPartnerRole
          ? `${collaboration.requesterRole} / ${collaboration.expectedPartnerRole}`
          : undefined;

      // Resolve grading result and section titles for section 7
      let gradingResult: FullGradingResult | null = null;
      let sectionTitleMap: Record<string, string> = {};
      let myAnswersForPdf: Record<string, any> | undefined;
      let partnerAnswersForPdf: Record<string, any> | undefined;
      let questionOptionMap: Record<string, { type: string; options: Array<{ id: string; label: string }> }> | undefined;

      if (collaboration?.requesterRole && collaboration?.expectedPartnerRole) {
        try {
          const questionnaireId = getQuestionnaireId(
            collaboration.requesterRole,
            collaboration.expectedPartnerRole
          );
          const questionnaire = QUESTIONNAIRE_MAP[questionnaireId];

          if (questionnaire) {
            // Build section title map and question option map
            questionOptionMap = {};
            for (const section of questionnaire.sections ?? []) {
              sectionTitleMap[section.id] = section.title;
              for (const q of section.questions) {
                questionOptionMap[q.id] = { type: q.type, options: q.options.map((o: any) => ({ id: o.id, label: o.label })) };
              }
            }

            // Fetch questionnaire responses
            const responseData = await trpc.collaboration.getQuestionnaireResponses.query({ matchId: id });

            if (responseData.bothSubmitted && responseData.myAnswers && responseData.partnerAnswers) {
              myAnswersForPdf = responseData.myAnswers as Record<string, any>;
              partnerAnswersForPdf = responseData.partnerAnswers as Record<string, any>;

              // Use cached grading result from server if available
              if (responseData.gradingResult) {
                gradingResult = responseData.gradingResult as unknown as FullGradingResult;
              } else {
                const isRequester = collaboration.requesterCompanyId === userDetails?.company?.id;
                const answersA = isRequester
                  ? (responseData.myAnswers as unknown as Record<string, Answer>)
                  : (responseData.partnerAnswers as unknown as Record<string, Answer>);
                const answersB = isRequester
                  ? (responseData.partnerAnswers as unknown as Record<string, Answer>)
                  : (responseData.myAnswers as unknown as Record<string, Answer>);
                gradingResult = gradeFullQuestionnaire(questionnaire, answersA, answersB);
              }
            }
          }
        } catch (err) {
          console.error("Failed to compute grading for PDF:", err);
        }
      }

      await generateCollaborationAlignmentReport({
        partyA: partyACompany,
        partyB: partyBCompany,
        sharedDocuments: sharedDocs,
        matchId: id,
        collaborationType,
        requesterRole: collaboration?.requesterRole,
        expectedPartnerRole: collaboration?.expectedPartnerRole,
        requesterCompanyId: collaboration?.requesterCompanyId,
        expressInterestTitle: collaboration?.expressInterestTitle,
        expressInterestSummary: collaboration?.expressInterestSummary,
        gradingResult,
        sectionTitleMap,
        myAnswers: myAnswersForPdf,
        partnerAnswers: partnerAnswersForPdf,
        questionOptionMap,
      });
    } catch (error) {
      console.error("Failed to generate report:", error);
    } finally {
      setDownloadingPDF(false);
    }
  };

  const tabIconColor = (tab: string): CSSProperties => ({
    color: activeTab === tab ? "#ffffff" : "var(--mantine-color-slate-6)",
  });

  const interestTabStyle = (tab: string) => ({
    backgroundColor: activeTab === tab ? "#04305c" : "#ebf0ff",
    color: activeTab === tab ? "white" : "var(--mantine-color-slate-6)",
    borderRadius: "var(--mantine-radius-md) var(--mantine-radius-md) 0 0",
    ...navyNavTabSegmentBorders(activeTab === tab),
    whiteSpace: "nowrap" as const,
    fontWeight: activeTab === tab ? 500 : 400,
    cursor: "pointer" as const,
    /** Match mobile strip row height so grid rows do not leave a square gap above shorter tabs (clips the look of the top-left radius on the first tab). */
    minHeight: isWorkspaceTabsMobile ? 56 : 44,
  });

  const tabRightSpacer = (
    <Box
      w={28}
      h={28}
      miw={28}
      mih={28}
      style={{ display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
    />
  );

  return (
    <Box
      style={{
        display: "flex",
        flexDirection: "column",
        ...(isWorkspaceTabsMobile ? { minHeight: "100vh" } : { height: "100vh" }),
      }}
    >
      {/* Header with Back Button and Title */}
      <Flex
        align="center"
        gap={{ base: "xs", sm: "md" }}
        px={{ base: "sm", sm: "md" }}
        py={{ base: "sm", sm: "md" }}
        wrap="nowrap"
        style={{ borderBottom: "1px solid #E5E7EB", flexShrink: 0 }}
      >
        {isWorkspaceTabsMobile ? (
          <ActionIcon
            variant="filled"
            color="primary"
            size="lg"
            radius="xl"
            onClick={handleWorkspaceBack}
            aria-label={workspaceBack.label}
          >
            <IconArrowLeft size={18} />
          </ActionIcon>
        ) : (
          <Button
            variant="filled"
            color="primary"
            leftSection={<IconArrowLeft size={16} />}
            size="sm"
            radius="xl"
            onClick={handleWorkspaceBack}
            style={{ flexShrink: 0 }}
          >
            {workspaceBack.label}
          </Button>
        )}
        <Text
          fw={500}
          fz={{ base: "md", sm: "xl" }}
          lh={1.25}
          style={{ flex: 1, minWidth: 0, textAlign: isWorkspaceTabsMobile ? "center" : undefined }}
        >
          Collaboration Workspace
        </Text>
        {isWorkspaceTabsMobile ? (
          <ActionIcon
            variant="filled"
            color="green"
            size="lg"
            radius="xl"
            onClick={handleDownloadProfile}
            disabled={workspaceLocked}
            loading={downloadingPDF}
            aria-label="Download complete report"
          >
            <IconDownload size={18} />
          </ActionIcon>
        ) : (
          <Button
            ml="auto"
            variant="filled"
            color="green"
            leftSection={<IconDownload size={18} />}
            loading={downloadingPDF}
            onClick={handleDownloadProfile}
            disabled={workspaceLocked}
            style={{ flexShrink: 0 }}
          >
            Download Complete Report
          </Button>
        )}
      </Flex>

      <Box
        px="md"
        pt="md"
        pb={isWorkspaceTabsMobile ? "xl" : undefined}
        style={{
          flex: isWorkspaceTabsMobile ? undefined : 1,
          display: "flex",
          flexDirection: "column",
          minHeight: isWorkspaceTabsMobile ? undefined : 0,
        }}
      >
        {showPreviewPaymentBanner ? (
          <CollaborationPaymentHeaderCard
            creditsRequired={engagementFeeCredits}
            onProceed={() => beginEngagementPayment(id)}
            loading={paymentLoading}
            userPaid={userPaid}
          />
        ) : null}
        {collaboration.status === CollaborationStatus.BOTH_PAID && collaboration.collaborationReadOnly ? (
          <Alert color="gray" title="Collaboration closed" mb="md" variant="light">
            The active period ended 45 days after both parties paid. You can still view this workspace, chat history, and
            documents. Sending messages, uploading files, updating the questionnaire, and signing the NDA are no longer
            available.
          </Alert>
        ) : null}
        {company && workspaceLocked && showPreviewPaymentBanner ? (
          <WorkspacePartnerSection
            company={company}
            collaboration={collaboration}
            onCollaborationChange={(next) => {
              setCollaboration(next);
              if (next?.id === id) void fetchCollaboration();
            }}
            onMatchCreated={handleMatchCreated}
            showPaymentModals={false}
            onRequestPayment={() => beginEngagementPayment(id)}
            hideCompanyHeaderCard
            hidePaymentBanner
          />
        ) : null}
        {showPartnerSectionAboveTabs ? (
          <WorkspacePartnerSection
            company={company}
            collaboration={collaboration}
            onCollaborationChange={(next) => {
              setCollaboration(next);
              if (next?.id === id) void fetchCollaboration();
            }}
            onMatchCreated={handleMatchCreated}
            showPaymentModals={false}
            onRequestPayment={() => beginEngagementPayment(id)}
          />
        ) : showPreviewPaymentBanner && loadingCompany ? (
          <Center py="md">
            <Loader size="sm" />
          </Center>
        ) : null}
        {/* Tabs — avoid overflow:hidden on ancestors so tab radii are not clipped; strip uses same horizontal padding as content (parent px="md"). */}
        <Tabs
          value={activeTab}
          onChange={handleWorkspaceTabChange}
          style={{
            display: "flex",
            flexDirection: "column",
            ...(isWorkspaceTabsMobile ? {} : { flex: 1, minHeight: 0 }),
          }}
        >
          <Box
            w="100%"
            style={{ ...NAV_TABS_FULL_WIDTH_LINE_STYLE, overflow: "visible" }}
          >
            <Tabs.List grow={!!isWorkspaceTabsMobile} styles={collaborationWorkspaceTabsListStyles}>
              <Tabs.Tab
                value="overview"
                aria-label="Overview"
                px={{ base: 4, sm: "lg" }}
                py="xs"
                fz={NAV_TAB_LABEL_FZ}
                style={{ ...NAV_TAB_EQUAL_WIDTH_CELL_STYLE, ...interestTabStyle("overview") }}
                styles={collaborationWorkspaceTabStyles}
                leftSection={
                  <Box hiddenFrom="sm" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <IconBuilding size={COLLAB_MOBILE_TAB_ICON} stroke={1.5} style={tabIconColor("overview")} />
                  </Box>
                }
                rightSection={<Box visibleFrom="sm">{tabRightSpacer}</Box>}
              >
                <Text
                  component="span"
                  fz={NAV_TAB_LABEL_FZ}
                  visibleFrom="sm"
                  c={activeTab === "overview" ? "#ffffff" : "var(--mantine-color-slate-6)"}
                  fw={activeTab === "overview" ? 500 : 400}
                >
                  Overview
                </Text>
              </Tabs.Tab>
              <Tabs.Tab
                value="assessment"
                aria-label="Assessment"
                px={{ base: 4, sm: "lg" }}
                py="xs"
                fz={NAV_TAB_LABEL_FZ}
                style={{ ...NAV_TAB_EQUAL_WIDTH_CELL_STYLE, ...interestTabStyle("assessment") }}
                styles={collaborationWorkspaceTabStyles}
                leftSection={
                  <Box hiddenFrom="sm" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <PartnershipHandshakeIcon size={COLLAB_MOBILE_TAB_ICON} style={tabIconColor("assessment")} />
                  </Box>
                }
                rightSection={
                  <Box
                    visibleFrom="sm"
                    w={28}
                    h={28}
                    miw={28}
                    mih={28}
                    style={{ display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    <Tooltip label={CFC_TAB_TOOLTIP} multiline maw={360} withArrow position="top">
                      <Box
                        component="span"
                        role="img"
                        aria-label="What is the Assessment?"
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        style={{ display: "inline-flex", alignItems: "center" }}
                      >
                        <IconInfoCircle
                          size={16}
                          stroke={1.5}
                          style={{
                            color: activeTab === "assessment" ? "#ffffff" : "var(--mantine-color-slate-6)",
                          }}
                        />
                      </Box>
                    </Tooltip>
                  </Box>
                }
              >
                <Text
                  component="span"
                  fz={NAV_TAB_LABEL_FZ}
                  visibleFrom="sm"
                  c={activeTab === "assessment" ? "#ffffff" : "var(--mantine-color-slate-6)"}
                  fw={activeTab === "assessment" ? 500 : 400}
                >
                  Assessment
                </Text>
              </Tabs.Tab>
              <Tabs.Tab
                value="documents"
                aria-label="Shared Documents"
                px={{ base: 4, sm: "lg" }}
                py="xs"
                fz={NAV_TAB_LABEL_FZ}
                style={{ ...NAV_TAB_EQUAL_WIDTH_CELL_STYLE, ...interestTabStyle("documents") }}
                styles={collaborationWorkspaceTabStyles}
                leftSection={
                  <Box hiddenFrom="sm" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <IconFileText size={COLLAB_MOBILE_TAB_ICON} stroke={1.5} style={tabIconColor("documents")} />
                  </Box>
                }
                rightSection={<Box visibleFrom="sm">{tabRightSpacer}</Box>}
              >
                <Text
                  component="span"
                  fz={NAV_TAB_LABEL_FZ}
                  visibleFrom="sm"
                  c={activeTab === "documents" ? "#ffffff" : "var(--mantine-color-slate-6)"}
                  fw={activeTab === "documents" ? 500 : 400}
                >
                  Shared Documents
                </Text>
              </Tabs.Tab>
            </Tabs.List>
          </Box>

          {showPartnerCardBelowTabs ? (
            <Box pt={isWorkspaceTabsMobile ? "sm" : "md"} pb="xs" style={{ flexShrink: 0 }}>
              <WorkspacePartnerProfileCard company={company} anonymized />
            </Box>
          ) : null}

          {/* Overview Tab */}
          <Tabs.Panel
            value="overview"
            pt={showPartnerCardBelowTabs ? (isWorkspaceTabsMobile ? "sm" : "md") : "xl"}
            style={isWorkspaceTabsMobile ? undefined : { flex: 1, overflow: "auto" }}
          >
            {loadingCompany ? (
              <Center h="100%">
                <Loader />
              </Center>
            ) : (
              company && (
                <MatchedProfileView
                  company={company}
                  containerSize="fluid"
                  mobileCollabWorkspaceCard={false}
                  suppressHeaderCard={suppressMatchedProfileHeader}
                  anonymousView={workspaceLocked}
                  workspaceCollaboration={collaboration}
                  questionnaireCta={
                    bothPaid &&
                    activeTab === "overview" &&
                    collaborationHasQuestionnaire &&
                    questionnaireMySubmitted !== null
                      ? questionnaireMySubmitted === false
                        ? { type: "start", onClick: handleGoToQuestionnaireTab }
                        : { type: "view", onClick: handleGoToQuestionnaireTab }
                      : null
                  }
                />
              )
            )}
          </Tabs.Panel>

          {/* Assessment tab — combined questionnaire + chat in one view */}
          <Tabs.Panel
            value="assessment"
            pt="md"
            style={
              isWorkspaceTabsMobile
                ? { display: "flex", flexDirection: "column", minHeight: "min(70vh, 640px)" }
                : { flex: 1, overflow: "hidden", display: "flex", flexDirection: "column", minHeight: 0 }
            }
          >
            {collaboration && userDetails?.company?.id && (
              <AssessmentTab
                matchId={id}
                collaboration={collaboration}
                userCompanyId={userDetails.company.id}
                currentUserId={userDetails?.id}
                chatOpen={bothPaid}
                collaborationReadOnly={effectiveReadOnly}
                workspaceLocked={workspaceLocked}
                autoFocusFirstUnanswered={autoFocusFirstUnansweredQuestion}
              />
            )}
          </Tabs.Panel>

          {/* Documents tab: shared files, NDA, and collaboration chat */}
          <Tabs.Panel
            value="documents"
            pt="md"
            style={
              isWorkspaceTabsMobile
                ? { display: "flex", flexDirection: "column", minHeight: "min(70vh, 640px)" }
                : { flex: 1, overflow: "hidden", display: "flex", flexDirection: "column", minHeight: 0 }
            }
          >
            {activeTab === "documents" && (
              workspaceLocked ? (
                <WorkspaceFeatureLockedPanel
                  title="Shared documents locked"
                  description={
                    collaboration.status === CollaborationStatus.REQUESTED
                      ? "Shared documents unlock after the interest request is accepted and both companies pay. Review the anonymized profile in Overview for now."
                      : userPaid
                        ? "You have paid. Documents unlock when the other company completes payment."
                        : "Document sharing and NDA are available after both companies complete payment. You can still review the anonymized company profile in Overview."
                  }
                  secondaryActionLabel={
                    !userPaid && isCollaborationPaymentPhase(collaboration) ? "Make payment" : undefined
                  }
                  onSecondaryAction={
                    !userPaid && isCollaborationPaymentPhase(collaboration)
                      ? () => beginEngagementPayment(id)
                      : undefined
                  }
                />
              ) : (
                <SharedDocumentsTab
                  matchId={id}
                  userCompanyName={userDetails?.company?.legalName}
                  ndaOtherCompanyId={otherCompanyId}
                  ndaCollaboration={collaboration}
                  collaborationReadOnly={effectiveReadOnly}
                  currentUserId={userDetails?.id}
                  chatOpen={bothPaid}
                  workspaceLocked={workspaceLocked}
                />
              )
            )}
          </Tabs.Panel>
        </Tabs>
      </Box>

      <PaymentMethodModal
        opened={paymentModalOpened}
        onClose={handlePaymentModalClose}
        onConfirm={handlePaymentConfirm}
        loading={paymentLoading}
        creditsRequired={engagementFeeCredits}
      />
      <CollaborationPaymentSuccessModal
        opened={!!successData}
        onClose={handleSuccessModalClose}
        paymentData={successData}
      />
    </Box>
  );
}
